import React from 'react';
import { Container, Card, Row, Col, Spinner, Badge, Button, Table, Modal, Form, Alert } from 'react-bootstrap';
import {
  ArrowLeftIcon,
  ClipboardDocumentListIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TruckIcon,
  XCircleIcon,
  ClockIcon,
  PhoneIcon,
  MapPinIcon,
  EnvelopeIcon,
  BuildingStorefrontIcon,
  ReceiptPercentIcon,
  CreditCardIcon,
  BanknotesIcon,
  EyeIcon,
  CubeIcon,
  TagIcon,
  FireIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCustomerDetail, statusColors } from './hooks/useCustomerDetail';

const CustomerDetail: React.FC = () => {
  const {
    navigate,
    isExpired,
    customer,
    orders,
    loading,
    selectedOrder,
    setSelectedOrder,
    stats,
    productStats,
    formatDate,
    fmt,
    canViewFinancials,
    payments,
    paymentsLoading,
    showPaymentModal,
    setShowPaymentModal,
    paymentForm,
    setPaymentForm,
    paymentSaving,
    paymentError,
    setPaymentError,
    handleAddPayment,
  } = useCustomerDetail();

  if (loading) {
    return (
      <CompanyLayout>
        <Container fluid className="py-5 text-center">
          <Spinner animation="border" variant="success" />
        </Container>
      </CompanyLayout>
    );
  }

  if (!customer) {
    return (
      <CompanyLayout>
        <Container fluid className="py-5 text-center">
          <p className="text-muted">Customer not found.</p>
          <Button variant="outline-success" size="sm" onClick={() => navigate('/company/customers')}>
            <ArrowLeftIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
            Back to Customers
          </Button>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {/* Back button */}
        <Button
          variant="link"
          className="p-0 mb-3 d-flex align-items-center gap-1 text-decoration-none"
          onClick={() => navigate('/company/customers')}
          style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}
        >
          <ArrowLeftIcon style={{ width: '14px', height: '14px' }} />
          Back to Customers
        </Button>

        {/* ─── Customer Profile Header ─── */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{
            background: 'linear-gradient(135deg, #065f46 0%, #059669 50%, #34d399 100%)',
            padding: '24px 24px 32px',
            marginBottom: '20px',
          }}>
            <div className="d-flex align-items-start gap-3">
              <div style={{
                width: '56px', height: '56px', borderRadius: '14px',
                background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 700, fontSize: '1.4rem',
                flexShrink: 0,
              }}>
                {customer.company_name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h4 className="fw-bold mb-0" style={{ color: 'white' }}>{customer.company_name}</h4>
                  <span style={{
                    backgroundColor: customer.is_active ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.3)',
                    color: 'white', fontSize: '0.72rem', fontWeight: 600,
                    padding: '3px 10px', borderRadius: '6px',
                  }}>
                    {customer.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.82rem', marginTop: '4px' }}>
                  {customer.customer_id}
                </div>
                <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
                  {customer.contact_person && (
                    <span className="d-flex align-items-center gap-1">
                      <UserCircleIcon style={{ width: '13px', height: '13px' }} />
                      {customer.contact_person}
                    </span>
                  )}
                  {customer.phone_number && (
                    <span className="d-flex align-items-center gap-1">
                      <PhoneIcon style={{ width: '13px', height: '13px' }} />
                      {customer.phone_number}
                    </span>
                  )}
                  {customer.email && (
                    <span className="d-flex align-items-center gap-1">
                      <EnvelopeIcon style={{ width: '13px', height: '13px' }} />
                      {customer.email}
                    </span>
                  )}
                  {customer.city && (
                    <span className="d-flex align-items-center gap-1">
                      <MapPinIcon style={{ width: '13px', height: '13px' }} />
                      {customer.city}{customer.postal_code ? `, ${customer.postal_code}` : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="d-none d-md-flex gap-2">
                <Button
                  size="sm"
                  onClick={() => navigate(`/company/orders?customer=${customer.id}`)}
                  style={{
                    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <ClipboardDocumentListIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  View Orders
                </Button>
              </div>
            </div>
          </div>

          {/* Financial cards row */}
          <Card.Body className="p-0">
            <Row className="g-0">
              {[
                {
                  icon: CurrencyDollarIcon,
                  label: 'Total Spent',
                  value: fmt(stats ? stats.totalRevenue : 0),
                  color: '#059669',
                  sub: stats ? `${stats.activeOrders} order${stats.activeOrders !== 1 ? 's' : ''}` : 'No orders',
                },
                {
                  icon: BanknotesIcon,
                  label: 'Amount Due',
                  value: fmt(customer.account_balance < 0 ? Math.abs(customer.account_balance) : 0),
                  color: customer.account_balance < 0 ? '#dc2626' : '#059669',
                  sub: customer.total_credit_limit > 0 ? `Limit: ${fmt(customer.total_credit_limit)}` : 'No credit limit',
                },
                {
                  icon: CreditCardIcon,
                  label: 'Balance',
                  value: fmt(customer.account_balance),
                  color: customer.account_balance >= 0 ? '#059669' : '#dc2626',
                  sub: 'Account balance',
                },
                {
                  icon: ReceiptPercentIcon,
                  label: 'Discount',
                  value: `${customer.discount_percentage}%`,
                  color: '#7c3aed',
                  sub: `Since ${formatDate(customer.created_at)}`,
                },
              ].map((card, i) => (
                <Col xs={6} md={3} key={card.label}>
                  <div className="mobile-stat-card" style={{
                    padding: '12px 16px',
                    borderRight: i < 3 ? '1px solid #f1f5f9' : 'none',
                    borderBottom: '1px solid #f1f5f9',
                  }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <card.icon style={{ width: '14px', height: '14px', color: card.color }} />
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{card.label}</span>
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: card.color }}>{card.value}</div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '1px' }}>{card.sub}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {/* ─── Order Statistics ─── */}
        {stats && (
          <Row className="g-2 g-md-3 mb-4">
            {/* Revenue */}
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100 mobile-stat-card" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CurrencyDollarIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Revenue</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#065f46', paddingLeft: '36px' }}>{fmt(stats.totalRevenue)}</div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', paddingLeft: '36px' }}>
                    excl. {stats.cancelled} cancelled
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Orders */}
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100 mobile-stat-card" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ShoppingCartIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Total Orders</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', paddingLeft: '36px' }}>{stats.totalOrders}</div>
                  <div className="d-flex flex-wrap gap-2" style={{ fontSize: '0.72rem', marginTop: '2px', paddingLeft: '36px' }}>
                    <span style={{ color: '#059669' }}>
                      <TruckIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{stats.delivered} delivered
                    </span>
                    {stats.cancelled > 0 && (
                      <span style={{ color: '#dc2626' }}>
                        <XCircleIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{stats.cancelled} cancelled
                      </span>
                    )}
                    {stats.active > 0 && (
                      <span style={{ color: '#d97706' }}>
                        <ClockIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{stats.active} active
                      </span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Average Order */}
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100 mobile-stat-card" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ChartBarIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Avg Order</span>
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', paddingLeft: '36px' }}>{fmt(stats.avgOrder)}</div>
                  <div className="d-flex flex-wrap gap-1 gap-md-3" style={{ fontSize: '0.72rem', marginTop: '2px', paddingLeft: '36px' }}>
                    <span style={{ color: '#059669' }}>
                      <ArrowTrendingUpIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{fmt(stats.largest)} max
                    </span>
                    <span style={{ color: '#94a3b8' }}>
                      <ArrowTrendingDownIcon style={{ width: '11px', height: '11px', marginRight: '2px' }} />{fmt(stats.smallest)} min
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Order History / Date Range */}
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm h-100 mobile-stat-card" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CalendarDaysIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Order History</span>
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', paddingLeft: '36px' }}>
                    {stats.firstOrder.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    {' — '}
                    {stats.lastOrder.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', paddingLeft: '36px' }}>
                    {Math.ceil((stats.lastOrder.getTime() - stats.firstOrder.getTime()) / (1000 * 60 * 60 * 24))} days span
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* ─── Product Breakdown ─── */}
        {productStats && (
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <Card.Body className="p-3 p-md-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CubeIcon style={{ width: '16px', height: '16px', color: '#059669' }} />
                </div>
                <h6 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '0.95rem' }}>Product Breakdown</h6>
                <span style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>
                  {productStats.totalItems.toLocaleString()} total items
                </span>
              </div>

              <Row className="g-3">
                {/* By Category */}
                <Col xs={12} md={6}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '8px' }}>
                    <TagIcon style={{ width: '12px', height: '12px', marginRight: '4px', color: '#64748b' }} />
                    By Category
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {productStats.byCategory.map((cat) => (
                      <div key={cat.name} style={{
                        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
                        padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px',
                      }}>
                        <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#065f46' }}>{cat.qty.toLocaleString()}</span>
                        <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{cat.name}</span>
                      </div>
                    ))}
                  </div>
                </Col>

                {/* Top 3 Products */}
                <Col xs={12} md={6}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '8px' }}>
                    <FireIcon style={{ width: '12px', height: '12px', marginRight: '4px', color: '#f59e0b' }} />
                    Top 3 Products
                  </div>
                  {productStats.topProducts.map((prod, i) => (
                    <div key={prod.name} className="d-flex align-items-center justify-content-between py-2" style={{
                      borderBottom: i < productStats.topProducts.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{
                          width: '22px', height: '22px', borderRadius: '6px',
                          background: i === 0 ? '#ecfdf5' : i === 1 ? '#f8fafc' : '#f8fafc',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.7rem', fontWeight: 700,
                          color: i === 0 ? '#065f46' : '#64748b',
                        }}>{i + 1}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{prod.name}</span>
                      </div>
                      <div className="text-end">
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#065f46' }}>{prod.qty.toLocaleString()}</span>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '8px' }}>{fmt(prod.revenue)}</span>
                      </div>
                    </div>
                  ))}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* ─── Address Info (if present) ─── */}
        {customer.address && (
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <Card.Body className="py-3 px-4">
              <div className="d-flex align-items-center gap-2 mb-1">
                <BuildingStorefrontIcon style={{ width: '15px', height: '15px', color: '#64748b' }} />
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Address</span>
              </div>
              <div style={{ fontSize: '0.88rem', color: '#334155' }}>{customer.address}</div>
              {(customer.city || customer.postal_code) && (
                <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>
                  {[customer.city, customer.postal_code].filter(Boolean).join(', ')}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ─── Payments Section ─── */}
        {canViewFinancials && (
          <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <Card.Body className="p-0">
              <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ borderBottom: '2px solid #e2e8f0' }}>
                <div className="d-flex align-items-center gap-2">
                  <BanknotesIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                  <h6 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '0.95rem' }}>Payments</h6>
                  <span style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>
                    {payments.length}
                  </span>
                  {customer.account_balance < 0 && (
                    <span style={{ backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>
                      Due: {fmt(Math.abs(customer.account_balance))}
                    </span>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => { setPaymentError(''); setShowPaymentModal(true); }}
                  disabled={isExpired}
                  style={{ fontSize: '0.78rem', fontWeight: 600, borderRadius: '6px' }}
                >
                  <CurrencyDollarIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                  Add Payment
                </Button>
              </div>

              {paymentsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" variant="success" />
                </div>
              ) : payments.length === 0 ? (
                <div className="text-center py-4">
                  <BanknotesIcon style={{ width: '36px', height: '36px', color: '#d1d5db' }} />
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>No payments recorded yet</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle" style={{ fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc' }}>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Date</th>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Amount</th>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }} className="d-none d-sm-table-cell">Method</th>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }} className="d-none d-md-table-cell">Reference</th>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }} className="d-none d-md-table-cell">Notes</th>
                        <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }} className="d-none d-lg-table-cell">Recorded By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 16px', color: '#64748b' }}>{formatDate(p.created_at)}</td>
                          <td style={{ padding: '10px 16px', fontWeight: 600, color: '#059669' }}>{fmt(p.amount)}</td>
                          <td style={{ padding: '10px 16px', color: '#64748b' }} className="d-none d-sm-table-cell">
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', backgroundColor: '#f0fdf4', color: '#065f46' }}>
                              {p.payment_method_display}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: '0.82rem' }} className="d-none d-md-table-cell">{p.reference_number || '—'}</td>
                          <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: '0.82rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} className="d-none d-md-table-cell">{p.notes || '—'}</td>
                          <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: '0.82rem' }} className="d-none d-lg-table-cell">{p.created_by || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* ─── Orders Table ─── */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          <Card.Body className="p-0">
            <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ borderBottom: '2px solid #e2e8f0' }}>
              <div className="d-flex align-items-center gap-2">
                <ClipboardDocumentListIcon style={{ width: '18px', height: '18px', color: '#059669' }} />
                <h6 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '0.95rem' }}>Order History</h6>
                <span style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.72rem', fontWeight: 600, padding: '3px 8px', borderRadius: '6px' }}>
                  {orders.length}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline-success"
                onClick={() => navigate(`/company/orders?customer=${customer.id}`)}
                style={{ fontSize: '0.78rem', fontWeight: 600, borderRadius: '6px' }}
              >
                View All Orders
              </Button>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-5">
                <ShoppingCartIcon style={{ width: '40px', height: '40px', color: '#d1d5db' }} />
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.88rem' }}>No orders yet</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle" style={{ fontSize: '0.84rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Order #</th>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Date</th>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Status</th>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }} className="d-none d-sm-table-cell">Items</th>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Amount</th>
                      <th style={{ color: '#475569', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 16px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const sc = statusColors[order.status] || { bg: '#f1f5f9', text: '#475569' };
                      return (
                        <tr key={order.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => setSelectedOrder(order)}>
                          <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1e293b' }}>
                            {order.order_number}
                          </td>
                          <td style={{ padding: '10px 16px', color: '#64748b' }}>
                            {formatDate(order.order_date)}
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            <span style={{
                              fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px',
                              borderRadius: '4px', backgroundColor: sc.bg, color: sc.text,
                              textTransform: 'capitalize',
                            }}>
                              {order.status}
                            </span>
                          </td>
                          <td className="d-none d-sm-table-cell" style={{ padding: '10px 16px', color: '#64748b' }}>
                            {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                          </td>
                          <td style={{ padding: '10px 16px', fontWeight: 600, color: '#1e293b' }}>
                            {fmt(order.total_amount)}
                            {order.original_total_amount && order.original_total_amount !== order.total_amount && (
                              <span style={{ fontSize: '0.7rem', color: '#94a3b8', textDecoration: 'line-through', marginLeft: '6px' }}>
                                {fmt(order.original_total_amount)}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 16px' }}>
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}
                              style={{ padding: '3px 8px', lineHeight: 1 }}
                            >
                              <EyeIcon style={{ width: '13px', height: '13px' }} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ─── Order Detail Modal ─── */}
        <Modal show={!!selectedOrder} onHide={() => setSelectedOrder(null)} centered size="lg">
          {selectedOrder && (
            <>
              <Modal.Header closeButton style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <Modal.Title style={{ fontSize: '1rem', color: '#1e293b' }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold">{selectedOrder.order_number}</span>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: '4px',
                      backgroundColor: (statusColors[selectedOrder.status] || { bg: '#f1f5f9' }).bg,
                      color: (statusColors[selectedOrder.status] || { text: '#475569' }).text,
                      textTransform: 'capitalize',
                    }}>
                      {selectedOrder.status}
                    </span>
                  </div>
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="d-flex flex-wrap gap-4 mb-3" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                  <span><strong>Order Date:</strong> {formatDate(selectedOrder.order_date)}</span>
                  {selectedOrder.delivery_date && <span><strong>Delivery:</strong> {formatDate(selectedOrder.delivery_date)}</span>}
                  {selectedOrder.delivered_at && <span><strong>Delivered:</strong> {formatDate(selectedOrder.delivered_at)}</span>}
                </div>
                {selectedOrder.notes && (
                  <div className="mb-3 p-2" style={{ backgroundColor: '#fffbeb', borderRadius: '6px', fontSize: '0.82rem', color: '#92400e' }}>
                    <strong>Notes:</strong> {selectedOrder.notes}
                  </div>
                )}
                <Table size="sm" hover className="mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ padding: '8px 12px', color: '#475569', fontWeight: 600 }}>Product</th>
                      <th style={{ padding: '8px 12px', color: '#475569', fontWeight: 600 }} className="d-none d-sm-table-cell">SKU</th>
                      <th style={{ padding: '8px 12px', color: '#475569', fontWeight: 600, textAlign: 'center' }}>Qty</th>
                      <th style={{ padding: '8px 12px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>Price</th>
                      <th style={{ padding: '8px 12px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map(item => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>{item.product.name}</td>
                        <td style={{ padding: '8px 12px', color: '#94a3b8' }} className="d-none d-sm-table-cell">{item.product.sku}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#1e293b' }}>
                          {item.quantity}
                          {item.original_quantity && item.original_quantity !== item.quantity && (
                            <span style={{ color: '#94a3b8', textDecoration: 'line-through', marginLeft: '4px', fontSize: '0.75rem' }}>{item.original_quantity}</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b' }}>{fmt(item.unit_price)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>{fmt(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e2e8f0' }}>
                      <td colSpan={3} className="d-none d-sm-table-cell"></td>
                      <td colSpan={1} className="d-sm-none"></td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>Total</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>
                        {fmt(selectedOrder.total_amount)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </Modal.Body>
            </>
          )}
        </Modal>

        {/* ─── Add Payment Modal ─── */}
        <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} centered>
          <Modal.Header closeButton style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <Modal.Title style={{ fontSize: '1rem', color: '#1e293b' }}>
              <div className="d-flex align-items-center gap-2">
                <BanknotesIcon style={{ width: '20px', height: '20px', color: '#059669' }} />
                Add Payment
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {paymentError && <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem' }}>{paymentError}</Alert>}
            <div className="mb-3 p-2" style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', fontSize: '0.82rem', color: '#065f46' }}>
              <strong>{customer.company_name}</strong>
              {customer.account_balance < 0 && (
                <span className="ms-2" style={{ color: '#dc2626' }}>
                  — Amount Due: {fmt(Math.abs(customer.account_balance))}
                </span>
              )}
            </div>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Amount *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={paymentForm.amount}
                onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                style={{ fontSize: '0.9rem' }}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Payment Method</Form.Label>
              <Form.Select
                value={paymentForm.payment_method}
                onChange={e => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                style={{ fontSize: '0.9rem' }}
              >
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Credit/Debit Card</option>
                <option value="other">Other</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Reference Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Check #, transaction ID, etc."
                value={paymentForm.reference_number}
                onChange={e => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                style={{ fontSize: '0.9rem' }}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional notes..."
                value={paymentForm.notes}
                onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                style={{ fontSize: '0.9rem' }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button variant="success" size="sm" onClick={handleAddPayment} disabled={paymentSaving}>
              {paymentSaving ? <Spinner animation="border" size="sm" /> : 'Add Payment'}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </CompanyLayout>
  );
};

export default CustomerDetail;
